import { Component, OnInit, ViewChild, TemplateRef, HostListener, ChangeDetectionStrategy } from "@angular/core";
import { BaseComponent, UserService, EventsService, EventBusService, environment } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { AdminEventFormComponent } from "./admin-event-form.component";
import type { CalendarOptions, EventClickArg } from "@fullcalendar/core";
import { take } from "rxjs/operators";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-admin-events-manager",
  templateUrl: "events-manager.component.html",
  standalone: false,
})
export class EventsManagerComponent extends BaseComponent implements OnInit {
  @ViewChild("deleteConfirmModal") deleteConfirmModal: TemplateRef<any>;
  public isCompact: boolean = window.innerWidth < 1200;
  public loading: boolean = false;
  public allEvents: any[] = [];
  public templates: Map<string, any> = new Map();
  public filteredEvents: any[] = [];
  public searchQuery: string = "";
  public statusFilter: string = "";
  public showPastEvents: boolean = false;
  public viewMode: string = "list";
  public eventToDelete: any = null;
  public deleteSeriesMode: boolean = false;
  public deletingIds: Set<string> = new Set();
  public expandedSeriesIds: Set<string> = new Set();
  public allSeriesExpanded: boolean = false;
  public hasSeriesEvents: boolean = false;
  private deleteModalRef: NgbModalRef;
  private cachedAllWithPast: any[] | null = null;

  public calendarOptions: CalendarOptions = {
    initialView: "dayGridMonth",
    headerToolbar: {
      left: "prev,next today",
      center: "title",
      right: "dayGridMonth,timeGridWeek",
    },
    events: [],
    eventClick: (info: EventClickArg) => this.onCalendarEventClick(info),
    dateClick: (info) => this.onDateClick(info),
    height: "calc(100vh - 200px)",
  };
  private lastDateClick: { date: string; time: number } = { date: "", time: 0 };

  @HostListener("window:resize")
  onResize() {
    this.isCompact = window.innerWidth < 1200;
  }

  constructor(
    userService: UserService,
    public appService: AppService,
    public eventsService: EventsService,
    private modalService: NgbModal,
    private eventBusService: EventBusService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.loadEvents();
  }

  loadEvents() {
    this.loading = true;
    this.cachedAllWithPast = null;
    this.eventsService.adminListEvents(undefined, this.showPastEvents).subscribe({
      next: (events) => {
        if (this.showPastEvents) {
          this.cachedAllWithPast = events;
        }
        this.processEvents(events);
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private processEvents(events: any[]) {
    this.templates.clear();
    const childParentIds = new Set<string>();
    for (const e of events) {
      if (e.parent_event_id) childParentIds.add(e.parent_event_id);
    }
    for (const e of events) {
      if (e.is_template || (e.recurring_rule && !e.parent_event_id)) {
        this.templates.set(e.id, e);
      }
    }
    const displayable = [];
    for (const e of events) {
      if (e.is_template || (e.recurring_rule && !e.parent_event_id)) {
        if (!childParentIds.has(e.id)) {
          displayable.push(this.enrichEvent(e));
        }
      } else {
        displayable.push(this.enrichEvent(e));
      }
    }
    this.allEvents = displayable;
    this.hasSeriesEvents = displayable.some((e) => e.parent_event_id);
    this.applyFilters();
    this.reExpandSeries();
    this.updateCalendarEvents();
    this.loading = false;
  }

  private reExpandSeries() {
    for (const event of this.filteredEvents) {
      const seriesId = event.parent_event_id;
      if (seriesId && this.expandedSeriesIds.has(seriesId)) {
        event._seriesEvents = this.buildSeriesEvents(seriesId);
      }
    }
  }

  private collapseRecurring(allEvents: any[]): any[] {
    const now = new Date().toISOString();
    const seriesMap = new Map<string, any[]>();

    // Group children by parent_event_id
    for (const e of allEvents) {
      if (e.parent_event_id) {
        if (!seriesMap.has(e.parent_event_id)) seriesMap.set(e.parent_event_id, []);
        seriesMap.get(e.parent_event_id)!.push(e);
      }
    }

    // Pick next upcoming instance per series
    const nextInstanceBySeries = new Map<string, any>();
    for (const [seriesId, children] of seriesMap) {
      const upcoming = children.filter((e) => e.start_datetime >= now || (e.end_datetime && e.end_datetime >= now)).sort((a, b) => a.start_datetime.localeCompare(b.start_datetime));
      const representative = upcoming.length > 0 ? upcoming[0] : children.sort((a, b) => b.start_datetime.localeCompare(a.start_datetime))[0];
      if (representative) nextInstanceBySeries.set(seriesId, representative);
    }

    // Compute effective status per series: "published" if any child is published
    const seriesStatus = new Map<string, string>();
    for (const [seriesId, children] of seriesMap) {
      for (const child of children) {
        if (child.status === "published") {
          seriesStatus.set(seriesId, "published");
          break;
        }
      }
      if (!seriesStatus.has(seriesId) && children.length > 0) {
        seriesStatus.set(seriesId, children[0].status);
      }
    }

    // Build collapsed list: standalone events + one representative per series
    const collapsed = allEvents.filter((e) => !e.parent_event_id);

    for (const [seriesId, instance] of nextInstanceBySeries) {
      const effectiveStatus = seriesStatus.get(seriesId);
      if (effectiveStatus && effectiveStatus !== instance.status) {
        instance._effectiveStatus = effectiveStatus;
      }
      collapsed.push(instance);
    }

    collapsed.sort((a, b) => (a.start_datetime || "").localeCompare(b.start_datetime || ""));

    return collapsed;
  }

  applyFilters() {
    let result = this.collapseRecurring(this.allEvents);
    if (this.statusFilter) {
      result = result.filter((e) => (e._effectiveStatus || e.status) === this.statusFilter);
    }
    if (this.searchQuery) {
      const q = this.searchQuery.toLowerCase();
      result = result.filter((e) => (e._displayTitle || "").toLowerCase().includes(q));
    }
    const d = new Date();
    const now = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    for (const e of result) {
      e._expanded = e.parent_event_id ? this.expandedSeriesIds.has(e.parent_event_id) : false;
      e._deleting = this.deletingIds.has(e.id);
      e._isPast = (e.end_datetime || e.start_datetime || "") < now;
      if (e._isPast) this.applyPastStatus(e);
      if (e._seriesEvents) {
        for (const s of e._seriesEvents) {
          s._deleting = this.deletingIds.has(s.id);
          s._isPast = (s.end_datetime || s.start_datetime || "") < now;
          if (s._isPast) this.applyPastStatus(s);
        }
      }
    }
    this.filteredEvents = result;
  }

  onSearchChange() {
    this.applyFilters();
  }

  onStatusChange() {
    this.applyFilters();
  }

  togglePastEvents() {
    this.showPastEvents = !this.showPastEvents;
    if (this.cachedAllWithPast) {
      this.processEvents(this.showPastEvents ? this.cachedAllWithPast : this.filterOutPast(this.cachedAllWithPast));
    } else {
      this.loadEvents();
    }
  }

  private filterOutPast(events: any[]): any[] {
    const d = new Date();
    const now = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    return events.filter((e) => e.is_template || e.recurring_rule || !e.start_datetime || (e.end_datetime || e.start_datetime) >= now);
  }

  private applyPastStatus(event: any) {
    if (!event._metadata) return;
    const statusEntry = event._metadata.find((m) => m.title === "Status" || m.title === "Next Occurrence Status");
    if (statusEntry) {
      statusEntry.value = "Completed";
      statusEntry.color = "";
    }
  }

  switchView(event: any) {
    this.viewMode = event.id;
  }

  private updateCalendarEvents() {
    const colorMap = { published: "#28a745", draft: "#ffc107", archived: "#6c757d" };
    this.calendarOptions = {
      ...this.calendarOptions,
      events: this.allEvents.map((e) => ({
        id: e.id,
        title: e._displayTitle,
        start: e.start_datetime,
        end: e.end_datetime,
        backgroundColor: colorMap[e.status] || "#007bff",
        borderColor: colorMap[e.status] || "#007bff",
        extendedProps: { event: e },
      })),
    };
  }

  onDateClick(info: any) {
    const now = Date.now();
    if (this.lastDateClick.date === info.dateStr && now - this.lastDateClick.time < 400) {
      const dateStr = info.dateStr.substring(0, 10);
      const modalRef = this.appService.showModal(AdminEventFormComponent, { size: "lg" });
      modalRef.componentInstance.startDatetime = dateStr + "T15:00";
      modalRef.componentInstance.endDatetime = dateStr + "T16:00";
      modalRef.componentInstance.timezone = "America/New_York";
      modalRef.componentInstance.saved.subscribe(() => {
        this.loadEvents();
      });
      this.lastDateClick = { date: "", time: 0 };
    } else {
      this.lastDateClick = { date: info.dateStr, time: now };
    }
  }

  onCalendarEventClick(info: EventClickArg) {
    const event = info.event.extendedProps.event;
    if (event) this.editEvent(event);
  }

  toggleSeries(event: any) {
    const seriesId = event.parent_event_id;
    if (!seriesId) return;
    if (this.expandedSeriesIds.has(seriesId)) {
      this.expandedSeriesIds.delete(seriesId);
      this.applyFilters();
      this.allSeriesExpanded = false;
      return;
    }
    this.expandedSeriesIds.add(seriesId);
    event._seriesEvents = this.buildSeriesEvents(seriesId);
    this.applyFilters();
  }

  toggleAllSeries() {
    if (this.allSeriesExpanded) {
      this.expandedSeriesIds.clear();
      this.allSeriesExpanded = false;
    } else {
      for (const event of this.filteredEvents) {
        const seriesId = event.parent_event_id;
        if (seriesId) {
          this.expandedSeriesIds.add(seriesId);
          event._seriesEvents = this.buildSeriesEvents(seriesId);
        }
      }
      this.allSeriesExpanded = true;
    }
    this.applyFilters();
  }

  private buildSeriesEvents(seriesId: string): any[] {
    return this.allEvents
      .filter((e) => e.parent_event_id === seriesId)
      .map((e) => {
        const copy = { ...e };
        const metadata = [];
        if (copy.start_datetime) {
          metadata.push({ title: "Date", value: new Date(copy.start_datetime).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) });
        }
        if (copy.status) {
          const colorMap = { published: "green", draft: "orange", archived: "gray" };
          metadata.push({ title: "Status", value: copy.status, color: colorMap[copy.status] || "" });
        }
        copy._metadata = metadata;
        copy._displayTitle = typeof copy.title === "string" ? copy.title : copy.title?.en || "";
        return copy;
      })
      .sort((a, b) => (a.start_datetime || "").localeCompare(b.start_datetime || ""));
  }

  private enrichEvent(event: any): any {
    event._displayTitle = typeof event.title === "string" ? event.title : event.title?.en || "";

    event._isRecurring = !!(event.parent_event_id || event.is_template || event.recurring_rule);
    if (event._isRecurring) event._displayTitle = "🔄 " + event._displayTitle;
    const subParts = [];
    if (event.slug && !event.parent_event_id) {
      subParts.push("/events/" + event.slug);
    } else if (event.parent_event_id) {
      const template = this.templates.get(event.parent_event_id);
      if (template?.slug) subParts.push("/events/" + template.slug);
    }
    event._slug = event.slug || (event.parent_event_id ? this.templates.get(event.parent_event_id)?.slug : null);
    event._subData = subParts.length ? subParts : null;

    const metadata = [];
    if (event.start_datetime) {
      const label = event.parent_event_id ? "Next Occurrence" : "Date";
      metadata.push({ title: label, value: new Date(event.start_datetime).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) });
    }
    if (event.status) {
      const colorMap = { published: "green", draft: "orange", archived: "gray" };
      const displayStatus = event._effectiveStatus || event.status;
      const statusLabel = event.parent_event_id ? "Next Occurrence Status" : "Status";
      metadata.push({ title: statusLabel, value: displayStatus, color: colorMap[displayStatus] || "" });
    }
    if (this.eventsService.isLivestreamEvent(event)) {
      const platform = this.eventsService.getLivestreamPlatform(event);
      metadata.push({ title: "Livestream", value: platform === "youtube" ? "YouTube" : "Vimeo" });
    }
    if (event.city || event.address) {
      metadata.push({ title: "Location", value: [event.city, event.state].filter(Boolean).join(", ") || "In-person" });
    }
    event._metadata = metadata;

    return event;
  }

  createEvent() {
    const modalRef = this.appService.showModal(AdminEventFormComponent, { size: "lg" });
    modalRef.componentInstance.saved.subscribe(() => {
      this.loadEvents();
    });
  }

  addOccurrence(event: any) {
    const modalRef = this.appService.showModal(AdminEventFormComponent, { size: "lg" });
    modalRef.componentInstance.addOccurrenceMode = true;
    modalRef.componentInstance.parentEvent = event;
    modalRef.componentInstance.saved.subscribe(() => {
      this.loadEvents();
    });
  }

  onMainItemClick(event: any) {
    if (event.parent_event_id) {
      // this.editSeriesTemplate(event);
      this.toggleSeries(event);
    } else {
      this.editEvent(event);
    }
  }

  editEvent(event: any) {
    const modalRef = this.appService.showModal(AdminEventFormComponent, { size: "lg" });
    modalRef.componentInstance.event = event;
    // A childless series (an intermittent one, or a template whose occurrences were all deleted)
    // still opens here, and its schedule editor needs the same occurrence list.
    modalRef.componentInstance.existingOccurrenceDates = this.allEvents.filter((e) => e.parent_event_id === event.id).map((e) => e.start_datetime);
    modalRef.componentInstance.saved.subscribe(() => {
      this.loadEvents();
    });
  }

  editChildEvent(event: any) {
    const modalRef = this.appService.showModal(AdminEventFormComponent, { size: "lg" });
    modalRef.componentInstance.event = event;
    modalRef.componentInstance.isChildMode = true;
    modalRef.componentInstance.saved.subscribe(() => {
      this.loadEvents();
    });
  }

  editSeriesTemplate(event: any) {
    const parentId = event.parent_event_id;
    if (!parentId) {
      this.editEvent(event);
      return;
    }
    const template = this.templates.get(parentId);
    if (!template) {
      this.editEvent(event);
      return;
    }
    const modalRef = this.appService.showModal(AdminEventFormComponent, { size: "lg" });
    modalRef.componentInstance.event = template;
    modalRef.componentInstance.isTemplateMode = true;
    // Lets the schedule editor count only the dates the series is actually missing.
    modalRef.componentInstance.existingOccurrenceDates = this.allEvents.filter((e) => e.parent_event_id === parentId).map((e) => e.start_datetime);
    modalRef.componentInstance.saved.subscribe(() => {
      this.loadEvents();
    });
  }

  deleteEvent(event: any, seriesMode: boolean = false) {
    if (event.start_datetime) {
      const parts = event.start_datetime.split("T");
      const [y, mo, d] = parts[0].split("-").map(Number);
      const [h, mi] = (parts[1] || "00:00").split(":").map(Number);
      const date = new Date(y, mo - 1, d, h, mi);
      event._deleteDate = date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    }
    this.eventToDelete = event;
    this.deleteSeriesMode = seriesMode;
    this.deleteModalRef = this.modalService.open(this.deleteConfirmModal, { size: "sm", centered: true });
  }

  confirmDelete(deleteAll: boolean) {
    this.deleteModalRef.close();
    const event = this.eventToDelete;
    const id = event.id;
    const isSeriesDelete = deleteAll || this.deleteSeriesMode;

    // For series delete, use template ID if available
    const deleteId = isSeriesDelete && event.parent_event_id ? event.parent_event_id : id;
    this.deletingIds.add(id);
    event._deleting = true;
    this.eventsService.adminDeleteEvent(deleteId, isSeriesDelete).subscribe({
      next: () => {
        this.deletingIds.delete(id);
        this.eventToDelete = null;
        this.deleteSeriesMode = false;
        this.loadEvents();
      },
      error: () => {
        this.deletingIds.delete(id);
        event._deleting = false;
        this.deleteSeriesMode = false;
      },
    });
  }

  cancelDelete() {
    this.deleteModalRef.close();
    this.eventToDelete = null;
    this.deleteSeriesMode = false;
  }

  copyEventUrl(event: any) {
    const slug = event._slug || event.slug;
    if (!slug) return;
    const url = environment.fenderUrl + "/events/" + slug;
    navigator.clipboard.writeText(url);
  }

  showEventActions(event: any) {
    const actions: string[] = [];
    if (event.parent_event_id || event.is_template || event.recurring_rule) {
      actions.push("Add Occurrence");
      // The series editor is where the recurring schedule lives (extending the end date creates
      // the missing occurrences), so it needs a way in on narrow screens too — "Edit" on a series
      // row expands the occurrence list rather than opening the template.
      if (event.parent_event_id) actions.push("Edit Series");
    }
    if (event._slug) actions.push("Copy URL");
    actions.push("Edit");
    if (!event._deleting) actions.push(event._isRecurring ? "Delete Series" : "Delete");

    this.eventBusService.emit(this.eventBusService.types.showActionChooser, { title: event._displayTitle, actions });
    this.eventBusService
      .observe(this.eventBusService.types.actionChosen)
      .pipe(take(1))
      .subscribe((result) => {
        if (!result) return;
        if (result === "Add Occurrence") this.addOccurrence(event);
        else if (result === "Edit Series") this.editSeriesTemplate(event);
        else if (result === "Copy URL") this.copyEventUrl(event);
        else if (result === "Edit") this.onMainItemClick(event);
        else if (result === "Delete Series") this.deleteEvent(event, true);
        else if (result === "Delete") this.deleteEvent(event, false);
      });
  }

  showChildEventActions(instance: any) {
    const actions: string[] = ["Edit"];
    if (!instance._deleting) actions.push("Delete");

    this.eventBusService.emit(this.eventBusService.types.showActionChooser, { title: instance._displayTitle, actions });
    this.eventBusService
      .observe(this.eventBusService.types.actionChosen)
      .pipe(take(1))
      .subscribe((result) => {
        if (!result) return;
        if (result === "Edit") this.editChildEvent(instance);
        else if (result === "Delete") this.deleteEvent(instance);
      });
  }
}
